/* dialogs.js — the platform's MODERN dialog system (2026-08-15 user mandate:
 * "the thing when you edit or delete says 'lhcstudio says' — make it modern,
 * centered, for the whole system").
 *
 * Drop-in replacements for the browser natives, promise-based:
 *   uiAlert(message, opts?)            → Promise<void>
 *   uiConfirm(message, opts?)          → Promise<boolean>
 *   uiPrompt(message, opts?)           → Promise<string|null>   (null = cancelled)
 *   uiChoice(message, choices, opts?)  → Promise<value|null>    (null = cancelled)
 *       choices: [{ value, label, hint?, danger? }] — one full-width button each
 *       (batch 14: "rename / move / delete" decisions that a yes/no cannot express)
 * opts: { title, okText, cancelText, danger, placeholder, initial }
 *
 * Plain DOM (no React) so ANY surface can call it — catalog, configurator,
 * store, admin. Centered card, backdrop blur, Enter/Escape keyboard, focus
 * management, light/dark aware (reads the page's data-theme / #lhcos). */

function isDark() {
  try {
    const b = document.body;
    if (document.querySelector("#lhcos")) return true;                 // technician: dark-locked
    const t = (b && b.dataset && b.dataset.theme) || (document.documentElement.dataset || {}).theme;
    if (t === "dark") return true;
    if (t === "light") return false;
    return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
  } catch (e) { return true; }
}

function openDialog({ kind, message, title, okText, cancelText, danger, placeholder, initial, choices }) {
  return new Promise((resolve) => {
    const dark = isDark();
    const C = dark
      ? { bg: "#1b1c20", text: "#f0efec", sub: "rgba(240,239,236,.66)", line: "rgba(255,255,255,.10)", field: "#121317", fieldLine: "rgba(255,255,255,.16)" }
      : { bg: "#ffffff", text: "#1f1e1b", sub: "rgba(31,30,27,.62)", line: "rgba(0,0,0,.08)", field: "#f6f5f2", fieldLine: "rgba(0,0,0,.14)" };
    const ACCENT = danger ? "#d9483b" : "#2563eb";

    const overlay = document.createElement("div");
    overlay.setAttribute("data-lhc-dialog", kind);
    overlay.style.cssText = "position:fixed;inset:0;z-index:99999;display:flex;align-items:center;justify-content:center;" +
      "background:rgba(10,10,12,.52);backdrop-filter:blur(6px);opacity:0;transition:opacity .16s ease;";
    const card = document.createElement("div");
    card.style.cssText = "width:min(440px,92vw);border-radius:16px;background:" + C.bg + ";color:" + C.text + ";" +
      "box-shadow:0 30px 90px rgba(0,0,0,.45);border:1px solid " + C.line + ";padding:22px 24px 18px;" +
      "font-family:Inter,Inter,system-ui,sans-serif;transform:translateY(10px) scale(.98);transition:transform .18s cubic-bezier(.2,.9,.3,1.2);";
    overlay.appendChild(card);

    const h = document.createElement("div");
    h.textContent = title || (kind === "confirm" ? "Are you sure?" : kind === "prompt" ? "Enter a value" : "Notice");
    h.style.cssText = "font-size:16px;font-weight:700;letter-spacing:.01em;margin-bottom:8px;";
    card.appendChild(h);

    if (message) {
      const m = document.createElement("div");
      m.textContent = String(message);
      m.style.cssText = "font-size:13.5px;line-height:1.55;color:" + C.sub + ";white-space:pre-line;margin-bottom:16px;";
      card.appendChild(m);
    }

    let input = null;
    if (kind === "prompt") {
      input = document.createElement("input");
      input.type = "text";
      input.value = initial != null ? String(initial) : "";
      input.placeholder = placeholder || "";
      input.style.cssText = "width:100%;box-sizing:border-box;padding:11px 13px;border-radius:10px;border:1px solid " + C.fieldLine + ";" +
        "background:" + C.field + ";color:" + C.text + ";font-size:14px;font-family:inherit;outline:none;margin-bottom:16px;";
      input.addEventListener("focus", () => { input.style.borderColor = ACCENT; });
      input.addEventListener("blur", () => { input.style.borderColor = C.fieldLine; });
      card.appendChild(input);
    }

    let pickChoice = null;
    if (kind === "choice") {
      const list = document.createElement("div");
      list.setAttribute("data-lhc-choices", "");
      list.style.cssText = "display:flex;flex-direction:column;gap:8px;margin-bottom:14px;";
      (choices || []).forEach((c) => {
        const b = document.createElement("button");
        b.type = "button";
        b.setAttribute("data-choice", String(c.value));
        const col = c.danger ? "#d9483b" : C.text;
        b.style.cssText = "display:flex;flex-direction:column;align-items:flex-start;gap:2px;width:100%;text-align:left;cursor:pointer;" +
          "padding:10px 13px;border-radius:10px;font-family:inherit;background:" + C.field + ";color:" + col + ";border:1px solid " +
          (c.danger ? "rgba(217,72,59,.45)" : C.fieldLine) + ";";
        const l = document.createElement("span");
        l.textContent = c.label;
        l.style.cssText = "font-size:13.5px;font-weight:700;";
        b.appendChild(l);
        if (c.hint) {
          const hh = document.createElement("span");
          hh.textContent = c.hint;
          hh.style.cssText = "font-size:12px;color:" + C.sub + ";";
          b.appendChild(hh);
        }
        b.addEventListener("mouseenter", () => { b.style.borderColor = c.danger ? "#d9483b" : ACCENT; });
        b.addEventListener("mouseleave", () => { b.style.borderColor = c.danger ? "rgba(217,72,59,.45)" : C.fieldLine; });
        b.addEventListener("click", () => pickChoice && pickChoice(c.value));
        list.appendChild(b);
      });
      card.appendChild(list);
    }

    const row = document.createElement("div");
    row.style.cssText = "display:flex;justify-content:flex-end;gap:10px;margin-top:2px;";
    card.appendChild(row);

    const mkBtn = (label, primary) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.textContent = label;
      btn.style.cssText = "padding:9px 18px;border-radius:10px;font-size:13.5px;font-weight:700;cursor:pointer;font-family:inherit;" +
        (primary
          ? "border:1px solid " + ACCENT + ";background:" + ACCENT + ";color:#fff;"
          : "border:1px solid " + C.fieldLine + ";background:transparent;color:" + C.text + ";");
      btn.addEventListener("mouseenter", () => { btn.style.filter = "brightness(1.08)"; });
      btn.addEventListener("mouseleave", () => { btn.style.filter = ""; });
      row.appendChild(btn);
      return btn;
    };

    let cancelBtn = null;
    if (kind !== "alert") cancelBtn = mkBtn(cancelText || "Cancel", false);
    // a choice dialog has no OK — every choice is its own button
    const okBtn = kind === "choice" ? null : mkBtn(okText || (kind === "confirm" ? (danger ? "Delete" : "OK") : "OK"), true);

    const prevFocus = document.activeElement;
    const close = (result) => {
      overlay.style.opacity = "0";
      card.style.transform = "translateY(10px) scale(.98)";
      window.removeEventListener("keydown", onKey, true);
      setTimeout(() => { overlay.remove(); try { prevFocus && prevFocus.focus && prevFocus.focus(); } catch (e) {} }, 160);
      resolve(result);
    };
    const ok = () => { if (kind !== "choice") close(kind === "prompt" ? (input ? input.value : "") : true); };
    const cancel = () => close(kind === "prompt" || kind === "choice" ? null : kind === "alert" ? undefined : false);
    pickChoice = (v) => close(v);

    if (okBtn) okBtn.addEventListener("click", ok);
    if (cancelBtn) cancelBtn.addEventListener("click", cancel);
    overlay.addEventListener("pointerdown", (e) => { if (e.target === overlay) cancel(); });
    const onKey = (e) => {
      if (e.key === "Escape") { e.stopPropagation(); cancel(); }
      else if (e.key === "Enter" && (kind !== "prompt" || e.target === input)) { e.stopPropagation(); ok(); }
    };
    window.addEventListener("keydown", onKey, true);

    document.body.appendChild(overlay);
    requestAnimationFrame(() => {
      overlay.style.opacity = "1";
      card.style.transform = "translateY(0) scale(1)";
      const f = input || okBtn || cancelBtn;
      if (f) f.focus();
      if (input && input.value) input.select();
    });
  });
}

export function uiAlert(message, opts = {}) { return openDialog({ kind: "alert", message, ...opts }); }
export function uiConfirm(message, opts = {}) { return openDialog({ kind: "confirm", message, ...opts }); }
export function uiPrompt(message, opts = {}) { return openDialog({ kind: "prompt", message, ...opts }); }
export function uiChoice(message, choices, opts = {}) { return openDialog({ kind: "choice", message, choices: choices || [], ...opts }); }
